import { appFetch } from "../../utils/appFetch";

export interface AppFetchResponse {
    data: any;
    status: number;
}

function unwrap<T>(res: AppFetchResponse): { ok: boolean; data?: T; error?: string } {
    const ok = res.status >= 200 && res.status < 300;
    const payload = res?.data;
    if (payload && typeof payload === "object" && "data" in payload) {
        return { ok, data: (payload as any).data as T, error: (payload as any).error };
    }
    return { ok, data: payload as T, error: (payload as any)?.error };
}

export const RegisterV2Onboarding = async (
    domain: string,
    friendlyName: string,
    method: "dns" | "html" | "meta",
    platformId: number,
    onboardingToken: string
) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/onboarding/register`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        domain,
        params: {
            domain,
            friendly_name: friendlyName,
            method,
            platform_id: platformId,
            onboarding_token: onboardingToken,
        },
    });
    return unwrap<{
        onboarding_id: number;
        verify_token: string;
        onboarding_token: string;
        method: string;
        domain: string;
    }>(res);
};

export const StartV2OnboardingContact = async (domain: string, input: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
}) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/onboarding/contact`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        domain,
        params: input,
    });
    return unwrap<{ onboarding_id: number; onboarding_token: string }>(res);
};

export const SendV2OnboardingEmailCode = async (domain: string, onboardingToken: string, email?: string) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/onboarding/email/send`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        domain,
        params: {
            onboarding_token: onboardingToken,
            email,
        },
    });
    return unwrap<{ ok: boolean; retry_after_seconds?: number }>(res);
};

export const VerifyV2OnboardingEmailCode = async (domain: string, onboardingToken: string, code: string) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/onboarding/email/verify`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        domain,
        params: {
            onboarding_token: onboardingToken,
            code,
        },
    });
    return unwrap<{ verified: boolean; retry_after_seconds?: number }>(res);
};

export const SendV2OnboardingPhoneCode = async (domain: string, onboardingToken: string, phone?: string) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/onboarding/phone/send`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        domain,
        params: {
            onboarding_token: onboardingToken,
            phone,
        },
    });
    return unwrap<{ ok: boolean; retry_after_seconds?: number }>(res);
};

export const VerifyV2OnboardingPhoneCode = async (domain: string, onboardingToken: string, code: string) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/onboarding/phone/verify`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        domain,
        params: {
            onboarding_token: onboardingToken,
            code,
        },
    });
    return unwrap<{ verified: boolean; retry_after_seconds?: number }>(res);
};

export const DetectV2OnboardingPlatform = async (domain: string, onboardingToken: string, websiteUrl: string) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/onboarding/detect-platform`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        domain,
        params: {
            onboarding_token: onboardingToken,
            website_url: websiteUrl,
        },
    });
    return unwrap<{ platform: "shopify" | "woocommerce" | "standalone"; platform_id: number }>(res);
};

export const SendV2ByonPhoneCode = async (domain: string, phone: { phoneNumber?: string; isoCountry?: string }) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/onboarding/byon/phone/send`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        domain,
        params: {
            phone,
        },
    });
    return unwrap<{ ok: boolean; phone?: string }>(res);
};

export const VerifyV2ByonPhoneCode = async (
    domain: string,
    code: string,
    phone: { phoneNumber?: string; isoCountry?: string }
) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/onboarding/byon/phone/verify`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        domain,
        params: {
            code,
            phone,
        },
    });
    return unwrap<{ verified: boolean; phone?: string }>(res);
};

export const RegisterV2OnboardingPhoneNumber = async (domain: string, input: {
    phone: string;
    price: number;
    iso_country: string;
    is_byon: boolean;
    token?: string;
    mock_twilio_purchase?: "success" | "failure";
}) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/onboarding/phone/register`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        domain,
        params: {
            phone: input,
            mock_twilio_purchase: input.mock_twilio_purchase,
        },
    });
    return unwrap<{ phone_id: number; is_byon: boolean }>(res);
};

export const GetV2OnboardingPhoneStatus = async (domain: string) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/onboarding/phone/status`,
        method: "GET",
        headers: { "Content-Type": "application/json" },
        domain,
    });
    return unwrap<{ has_phone: boolean; phone?: any; pending_phone?: { phoneNumber?: string; isoCountry?: string; price?: number; token?: string } | null }>(res);
};

export const VerifyV2Onboarding = async (domain: string, onboardingId: number, onboardingToken: string) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/onboarding/verify`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        domain,
        params: {
            onboarding_id: onboardingId,
            onboarding_token: onboardingToken,
        },
    });
    return unwrap<{ verified: boolean; shop_id?: number; domain?: string; user?: any }>(res);
};

export const CreateV2OnboardingApiKey = async (domain: string, onboardingToken: string, type: "public" | "secret", allowedOrigins: string[]) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/onboarding/api-keys`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        domain,
        params: {
            onboarding_token: onboardingToken,
            type,
            allowed_origins: allowedOrigins,
        },
    });
    return unwrap<{ key: string; key_prefix: string }>(res);
};
